
// src/features/dashboard/mocks/activityDetails.mock.ts

import type {
  KPIItem,
  DistributionItem,
  ApplicationItem,
  EmployeeDayDetailsResponse,
} from '../types/activityDetails.types';

/**
 * =========================================
 * FORMAT HOURS
 * =========================================
 */
export const formatHours = (
  hours: number,
): string => {

  if (!hours || hours <= 0) {
    return '00:00';
  }

  const totalMinutes =
    Math.round(hours * 60);

  const hrs =
    Math.floor(
      totalMinutes / 60,
    );

  const mins =
    totalMinutes % 60;

  return `${hrs
    .toString()
    .padStart(2, '0')}:${mins
    .toString()
    .padStart(2, '0')}`;
};

/**
 * =========================================
 * FORMAT TIME
 * =========================================
 */
export const formatTime = (
  dateString: string,
): string => {

  if (!dateString) {
    return '--:--';
  }

  try {

    return new Date(
      dateString,
    ).toLocaleTimeString(
      [],
      {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      },
    );

  } catch {

    return '--:--';
  }
};

/**
 * =========================================
 * FORMAT DATE
 * =========================================
 */
export const formatDate = (
  dateString: string,
): string => {

  if (!dateString) {
    return '';
  }

  try {

    return new Date(
      dateString,
    ).toLocaleDateString(
      'en-GB',
      {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        weekday: 'long',
      },
    );

  } catch {

    return '';
  }
};

/**
 * =========================================
 * KPI CARDS
 * =========================================
 */
export const getKpiCards = (
  details: EmployeeDayDetailsResponse,
): KPIItem[] => [

  {
    title: 'ACTIVE HOURS',

    value: formatHours(
      details.summary
        ?.toT_ACTIVEHOURS || 0,
    ),

    color: 'text-[#10B981]',
  },

  {
    title: 'PRODUCTIVE HOURS',

    value: formatHours(
      details.summary
        ?.toT_PRODHOURS || 0,
    ),

    color: 'text-[#2563EB]',
  },

  {
    title: 'IDLE TIME',

    value: formatHours(
      details.summary
        ?.toT_IDLETIME || 0,
    ),

    color: 'text-[#F97316]',
  },

  {
    title: 'FIRST → LAST',

    value: `${formatTime(
      details.summary
        ?.firstlogin || '',
    )} → ${formatTime(
      details.summary
        ?.lastlogout || '',
    )}`,

    color: 'text-[#64748B]',
  },
];

/**
 * =========================================
 * DISTRIBUTION ITEMS
 * NOW USING API DISTRIBUTION OBJECT
 * =========================================
 */
export const getDistributionItems = (
  details: EmployeeDayDetailsResponse,
): DistributionItem[] => {

  const distribution =
    details?.distribution;

  if (!distribution) {
    return [];
  }

  return [

    {
      color: '#22C55E',

      label: 'Productive',

      percentage: `${distribution.productivePercentage || 0}%`,

      width: `${distribution.productivePercentage || 0}%`,
    },

    {
      color: '#2563EB',

      label: 'Neutral',

      percentage: `${distribution.neutralPercentage || 0}%`,

      width: `${distribution.neutralPercentage || 0}%`,
    },

    {
      color: '#F97316',

      label: 'Non-productive',

      percentage: `${distribution.nonProductivePercentage || 0}%`,

      width: `${distribution.nonProductivePercentage || 0}%`,
    },

    {
      color: '#D1D5DB',

      label: 'Idle',

      percentage: `${distribution.idlePercentage || 0}%`,

      width: `${distribution.idlePercentage || 0}%`,
    },
  ];
};

/**
 * =========================================
 * STATUS COLOR
 * =========================================
 */
export const getStatusColor = (
  status: string,
): string => {

  switch (
    status?.toLowerCase()
  ) {

    case 'productive':

      return `
        bg-[#DCFCE7]
        text-[#166534]
      `;

    case 'neutral':

      return `
        bg-[#DBEAFE]
        text-[#1D4ED8]
      `;

    case 'non-productive':

      return `
        bg-[#FFF1E8]
        text-[#D46B08]
      `;

    case 'idle':

      return `
        bg-[#F3F4F6]
        text-[#667085]
      `;

    default:

      return `
        bg-[#F3F4F6]
        text-[#667085]
      `;
  }
};

/**
 * =========================================
 * APPLICATIONS
 * =========================================
 */
export const getApplications = (
  details: EmployeeDayDetailsResponse,
): ApplicationItem[] => {

  return (
    details?.applications || []
  ).map(
    (item) => ({

      initials:
        item.applicationName
          ?.split(' ')
          .map(
            (word) =>
              word[0],
          )
          .join('')
          .substring(0, 2)
          .toUpperCase() || '--',

      color:
        item.colorHex || '#64748B',

      app:
        item.applicationName,

      time:
        formatHours(
          item.durationHours || 0,
        ),

      status:
        item.productivityType,

      statusColor:
        getStatusColor(
          item.productivityType,
        ),
    }),
  );
};
